import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Smartphone, Mail, Loader2, Key, Copy, CheckCircle, RefreshCw, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MfaEnrollment } from './MfaEnrollment';

interface MfaSettingsProps {
  userId: string;
  userEmail: string;
  currentMethod: string | null;
  onMethodChange: () => void;
}

// Helper to parse methods from comma-separated string
const parseMethods = (methodString: string | null): string[] => {
  if (!methodString) return [];
  return methodString.split(',').filter(m => m.trim().length > 0);
};

export function MfaSettings({ userId, userEmail, currentMethod, onMethodChange }: MfaSettingsProps) {
  const [showEnrollment, setShowEnrollment] = useState(false);
  const [enrollmentMethod, setEnrollmentMethod] = useState<'totp' | 'email' | null>(null);
  const [backupCodesCount, setBackupCodesCount] = useState<number | null>(null);
  const [isLoadingBackupCount, setIsLoadingBackupCount] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [isGeneratingCodes, setIsGeneratingCodes] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [isDisabling, setIsDisabling] = useState<string | null>(null);

  // Parse the current methods
  const enabledMethods = parseMethods(currentMethod);
  const hasTotpEnabled = enabledMethods.includes('totp');
  const hasEmailEnabled = enabledMethods.includes('email');
  const hasAnyMfa = enabledMethods.length > 0;

  useEffect(() => {
    if (hasAnyMfa) {
      fetchBackupCodesCount();
    }
  }, [currentMethod, userId]);

  const fetchBackupCodesCount = async () => {
    setIsLoadingBackupCount(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-mfa-code', {
        body: { action: 'get-backup-codes-count', user_id: userId },
      });

      if (error) throw error;
      setBackupCodesCount(data?.count ?? 0);
    } catch (err) {
      console.error('Error fetching backup codes count:', err);
      setBackupCodesCount(0);
    } finally {
      setIsLoadingBackupCount(false);
    }
  };

  const generateBackupCodes = async () => {
    setIsGeneratingCodes(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-mfa-code', {
        body: { action: 'generate-backup-codes', user_id: userId },
      });

      if (error) throw error;
      
      setBackupCodes(data?.codes || []);
      setShowBackupCodes(true);
      await fetchBackupCodesCount();
      toast.success('Backup codes generated successfully');
    } catch (err) {
      console.error('Error generating backup codes:', err);
      toast.error('Failed to generate backup codes');
    } finally {
      setIsGeneratingCodes(false);
    }
  };

  const copyCode = async (code: string, index: number) => {
    await navigator.clipboard.writeText(code);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const copyAllCodes = async () => {
    const codesText = backupCodes.join('\n');
    await navigator.clipboard.writeText(codesText);
    toast.success('All codes copied to clipboard');
  };

  const handleEnableMethod = (method: 'totp' | 'email') => {
    setEnrollmentMethod(method);
    setShowEnrollment(true);
  };

  const handleDisableMethod = async (method: 'totp' | 'email') => {
    // Prevent disabling if it's the only method
    if (enabledMethods.length === 1 && enabledMethods.includes(method)) {
      toast.error('You must have at least one MFA method enabled');
      return;
    }

    setIsDisabling(method);
    try {
      // Remove the method from the list
      const newMethods = enabledMethods.filter(m => m !== method);
      
      // If disabling TOTP, unenroll from Supabase MFA
      if (method === 'totp') {
        const { data: factorsData } = await supabase.auth.mfa.listFactors();
        const totpFactors = factorsData?.totp || [];
        
        for (const factor of totpFactors) {
          await supabase.auth.mfa.unenroll({ factorId: factor.id });
        }
      }

      // Update the profile
      await supabase
        .from('profiles')
        .update({ mfa_method: newMethods.length > 0 ? newMethods.join(',') : null })
        .eq('user_id', userId);

      // Send notification
      await supabase.functions.invoke('send-mfa-code', {
        body: { action: 'notify-mfa-change', user_id: userId, change_type: 'disabled', method },
      });

      toast.success(`${method === 'totp' ? 'Authenticator App' : 'Email Verification'} disabled`);
      onMethodChange();
    } catch (err) {
      console.error('Error disabling MFA method:', err);
      toast.error('Failed to disable MFA method');
    } finally {
      setIsDisabling(null);
    }
  };

  const handleEnrollmentComplete = () => {
    setShowEnrollment(false);
    setEnrollmentMethod(null);
    onMethodChange();
    // Generate backup codes after enrollment if none exist
    if (backupCodesCount === 0 || backupCodesCount === null) {
      generateBackupCodes();
    }
  };

  if (showEnrollment && enrollmentMethod) {
    return (
      <Card className="cyber-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Enable Additional MFA Method
          </CardTitle>
          <CardDescription>
            {enrollmentMethod === 'totp' 
              ? 'Set up authenticator app for two-factor authentication'
              : 'Set up email verification for two-factor authentication'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MfaEnrollment
            userId={userId}
            email={userEmail}
            initialMethod={enrollmentMethod}
            onComplete={handleEnrollmentComplete}
            showBackButton={true}
            onBack={() => {
              setShowEnrollment(false);
              setEnrollmentMethod(null);
            }}
            existingMethods={enabledMethods}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="cyber-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add extra layers of security to your account. You can enable multiple methods.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current MFA Status */}
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50">
            <div className="flex items-center gap-3">
              <ShieldCheck className={`h-5 w-5 ${hasAnyMfa ? 'text-primary' : 'text-muted-foreground'}`} />
              <div>
                <p className="font-medium">
                  {hasAnyMfa 
                    ? `${enabledMethods.length} MFA Method${enabledMethods.length > 1 ? 's' : ''} Active`
                    : 'MFA Not Enabled'
                  }
                </p>
                <p className="text-sm text-muted-foreground">
                  {hasAnyMfa 
                    ? 'Your account is protected with two-factor authentication'
                    : 'Enable MFA to secure your account'
                  }
                </p>
              </div>
            </div>
            {hasAnyMfa && (
              <div className="flex gap-1">
                {hasTotpEnabled && (
                  <Badge variant="outline" className="border-primary/50 text-primary">
                    <Smartphone className="h-3 w-3 mr-1" />
                    App
                  </Badge>
                )}
                {hasEmailEnabled && (
                  <Badge variant="outline" className="border-primary/50 text-primary">
                    <Mail className="h-3 w-3 mr-1" />
                    Email
                  </Badge>
                )}
              </div>
            )}
          </div>

          {/* MFA Options */}
          <div className="grid gap-3">
            {/* Authenticator App Option */}
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50">
              <div className="flex items-center gap-3">
                <Smartphone className={`h-5 w-5 ${hasTotpEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
                <div>
                  <p className="font-medium">Authenticator App</p>
                  <p className="text-sm text-muted-foreground">Use Google Authenticator, Authy, or similar</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {hasTotpEnabled ? (
                  <>
                    <Badge variant="secondary">Enabled</Badge>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          disabled={isDisabling === 'totp' || enabledMethods.length === 1}
                          className="text-destructive hover:text-destructive"
                        >
                          {isDisabling === 'totp' ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Disable Authenticator App?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will remove the authenticator app as an MFA method. You will need to set it up again if you want to use it later.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDisableMethod('totp')}
                            className="bg-destructive hover:bg-destructive/90"
                          >
                            Disable
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                ) : (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEnableMethod('totp')}
                  >
                    Enable
                  </Button>
                )}
              </div>
            </div>

            {/* Email Option */}
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50">
              <div className="flex items-center gap-3">
                <Mail className={`h-5 w-5 ${hasEmailEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
                <div>
                  <p className="font-medium">Email Verification</p>
                  <p className="text-sm text-muted-foreground">Receive a code via email when you sign in</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {hasEmailEnabled ? (
                  <>
                    <Badge variant="secondary">Enabled</Badge>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          disabled={isDisabling === 'email' || enabledMethods.length === 1}
                          className="text-destructive hover:text-destructive"
                        >
                          {isDisabling === 'email' ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <X className="h-4 w-4" />
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Disable Email Verification?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will remove email verification as an MFA method. You will need to set it up again if you want to use it later.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDisableMethod('email')}
                            className="bg-destructive hover:bg-destructive/90"
                          >
                            Disable
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                ) : (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEnableMethod('email')}
                  >
                    Enable
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Backup Codes Section */}
          {hasAnyMfa && (
            <div className="pt-4 border-t border-border">
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50">
                <div className="flex items-center gap-3">
                  <Key className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Backup Codes</p>
                    <p className="text-sm text-muted-foreground">
                      {isLoadingBackupCount ? (
                        'Loading...'
                      ) : backupCodesCount === null || backupCodesCount === 0 ? (
                        'No backup codes generated'
                      ) : (
                        `${backupCodesCount} unused codes remaining`
                      )}
                    </p>
                  </div>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={isGeneratingCodes}
                    >
                      {isGeneratingCodes ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : backupCodesCount === 0 || backupCodesCount === null ? (
                        'Generate'
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Regenerate
                        </>
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {backupCodesCount === 0 || backupCodesCount === null 
                          ? 'Generate Backup Codes?' 
                          : 'Regenerate Backup Codes?'
                        }
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {backupCodesCount === 0 || backupCodesCount === null 
                          ? 'This will create 10 one-time use backup codes for account recovery.'
                          : 'This will invalidate all existing backup codes and generate 10 new ones. Make sure to save the new codes.'
                        }
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={generateBackupCodes}>
                        {backupCodesCount === 0 || backupCodesCount === null ? 'Generate' : 'Regenerate'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onOpenChange={setShowBackupCodes}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-primary" />
              Your Backup Codes
            </DialogTitle>
            <DialogDescription>
              Save these codes in a secure location. Each code can only be used once.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-2 bg-muted/50 rounded border border-border font-mono text-sm"
                >
                  <span>{code}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => copyCode(code, index)}
                  >
                    {copiedIndex === index ? (
                      <CheckCircle className="h-4 w-4 text-primary" />
                    ) : (
                      <Copy className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
            <Button onClick={copyAllCodes} variant="outline" className="w-full">
              <Copy className="h-4 w-4 mr-2" />
              Copy All Codes
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              These codes will not be shown again. Store them securely.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
